import React from "react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import stylesV2 from "./NearbyDoctorAvailabilitySection.module.scss";
import stylesV3 from "./NearbyDoctorAvailabilitySectionV2.module.scss";
import * as paths from "features/routing/paths";
import { DoctorAvatarStack } from "components/DoctorAvatarStack";
import { ExamType } from "types/examType";
import { DateString } from "types/dateString";
import { Doctor } from "types/doctor";
import { Office } from "types/office";
import { PatientReturnStatus } from "types/patientReturnStatus";
import { useSchedulerTracking } from "hooks/useSchedulerTracking";
import { useSchedulingABVariant } from "../utils/useSchedulingABVariant";

interface DateLinkProps {
  office: Office;
  date?: DateString;
  examType: ExamType;
  patientReturnStatus: PatientReturnStatus;
  onClick?: React.MouseEventHandler;
  version?: number;
}

export const DateLink: React.FC<DateLinkProps> = ({
  office,
  date,
  examType,
  patientReturnStatus,
  onClick,
  version,
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  const text = date ? dayjs(date).format("ddd., MMM. D") : "Available Today";
  let officePath = paths.appointmentsAvailableAppointments({
    officeId: office.id,
    examType,
    patientReturnStatus,
    query: { date },
  });
  if (version === 2) {
    officePath = date
      ? paths.v2appointmentStart(office.id, date)
      : paths.appointmentBookingStartV2({ officeId: office.id });
  }
  return (
    <Link to={officePath} className={styles.date} onClick={onClick}>
      {text}
    </Link>
  );
};

interface NearbyDoctorAvailabilitySectionProps {
  office: Office;
  doctors: Doctor[];
  date?: DateString;
  examType: ExamType;
  patientReturnStatus: PatientReturnStatus;
  selectedDoctorId?: string;
  version?: number;
}

export const NearbyDoctorAvailabilitySection: React.FC<
  NearbyDoctorAvailabilitySectionProps
> = ({
  office,
  doctors,
  date,
  examType,
  patientReturnStatus,
  selectedDoctorId,
  version,
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  const count = doctors.length;
  const { trackSchedulerEvent } = useSchedulerTracking(selectedDoctorId);

  if (count === 0) {
    return null;
  }

  const doctorsLabel = count === 1 ? "1 Doctor " : `${count} Doctors `;
  const datelabel = date ? "available starting " : null;

  return (
    <section className={!version ? styles.section : styles.sectionV2}>
      {!version && (
        <DoctorAvatarStack
          doctors={doctors.slice(0, 10)}
          callFrom={"nearby"}
          office={office}
        />
      )}
      <span className={styles.label}>
        {doctorsLabel}
        {datelabel}
        <DateLink
          office={office}
          date={date}
          examType={examType}
          patientReturnStatus={patientReturnStatus}
          version={version}
          onClick={(event) => {
            if (version === 2) event.stopPropagation();
            trackSchedulerEvent("browse-nearby-offices");
          }}
        />
      </span>
    </section>
  );
};
